import parseUrl from 'url-parse';

import { determineActiveField } from 'util/env';
import {
  getActiveUserId,
  getActiveFieldId,
  getActiveScriptId,
  getActiveScriptType
} from 'current/selectors';
import { getRouteVersion } from 'settings/selectors';

import { scriptTypes } from 'constants/scripts';
import { isPluginMode } from './isPluginMode';
import { getScriptCreatorUrl } from './urls';
import { getSearchParamOnPluginURL } from './queryString';
import { CROP_PLAN_INTEGRATION } from 'constants/cropPlanning';

const versionRoute = ({ state, route }) => {
  const routeVersion = getRouteVersion(state);
  const splitRoute = route.split('/');
  if (routeVersion) {
    splitRoute.splice(2, 0, routeVersion);
  }
  return splitRoute.join('/');
};

const shouldAddPluginPrefix = () => {
  if(isPluginMode()) return '/variable-rate';
  return '';
};

const shouldAddClientId = (userId) => {
  return isPluginMode() ? `?client-id=${userId}` : '';
};

/**
 * @param {string} key
 * @param {string} value
 * @returns A query param string, starting with ? or & depending on existing query params
 */
export const createQueryParam = (key, value) => {
  const queryString = window.location.hash.split('?')[1];
  const queryParamPrefix = queryString ? '&' : '?';

  return `${queryParamPrefix}${encodeURIComponent(key)}=${encodeURIComponent(value)}`;
};

const shouldAddCropPlanningIntegration = () => {
  return getSearchParamOnPluginURL(CROP_PLAN_INTEGRATION.queryParamKey)
    ? createQueryParam(CROP_PLAN_INTEGRATION.queryParamKey, 'true')
    : '';
};

export const routeParse = (hashRoute) => {
  let route = isPluginMode() ? hashRoute.replace('/variable-rate', '') : hashRoute;
  let clientId;
  let field;
  let mode;
  let version;
  let scriptType;
  let step;
  let scriptId;
  let parts;
  // umm....yeah....
  // TODO: Make this maintainable
  if (route.match(/(manage|edit|create)/)) {
    parts = route.match(
      /^#?\/(manage|edit|create)\/((v\d)\/)?(\d+)\/([^\/]+)(\/([^?]*))?/
    );
    if (parts) {
      [, mode, , version, clientId, field, , scriptId] = parts; // NOT a syntax error!
    }
  } else if (route.match(/manual/)) {
    parts = route.match(
      /^#?\/(manual)\/((v\d)\/)?(\w+)\/(\d)\/(\d+)\/(\d+)(\/([^?]*))?/
    );
    if (parts) {
      [, mode, , version, scriptType, step, clientId, field, , scriptId]
        = parts; // NOT a syntax error!
    }
  } else if (route.match(/experiment/)) {
    parts = route.match(
      /^#?\/(experiment)\/((v\d)\/)?(\w+)\/(\d)\/(\d+)\/(\d+)(\/([^?]*))?/
    );
    if (parts) {
      [, mode, , version, scriptType, step, clientId, field, , scriptId]
        = parts; // NOT a syntax error!
    }
  } else if (route.match(/advanced/)) {
    parts = route.match(
      /^#?\/(advanced)\/((v\d)\/)?((\w+)\/)?(\d)\/(\d+)\/(\d+)(\/([^?]*))?/
    );
    if (parts) {
      [, mode, , version, , scriptType, step, clientId, field, , scriptId]
        = parts; // NOT a syntax error!
    }
  }

  return { clientId, field, scriptType, step, mode, scriptId, version };
};

export const getAdvancedScriptingRoute = (state, step = 1) => {
  const userId = getActiveUserId(state);
  const fieldId = getActiveFieldId(state);
  const scriptType = getActiveScriptType(state);

  if (!userId || !fieldId) {
    return ''; // Hopefully will bounce to usable url
  }

  const path = `${shouldAddPluginPrefix()}/advanced/${scriptType}/${step}/${userId}/${fieldId}`;
  const queryParams = `${shouldAddClientId(userId)}${shouldAddCropPlanningIntegration()}`;

  return versionRoute({
    state,
    route: `${path}${queryParams}`
  });
};

export const getCreateRouteForUserIdFieldId = ({
  state,
  userId,
  fieldId
} = {}) => {
  if (!userId || !fieldId) {
    return ''; // Hopefully will bounce to usable url
  }

  return versionRoute({
    state,
    route: `${shouldAddPluginPrefix()}/create/${userId}/${fieldId}${shouldAddClientId(userId)}`
  });
};

export const getManualScriptingRoute = (state, step = 1) => {
  const userId = getActiveUserId(state);
  const fieldId = getActiveFieldId(state);
  const scriptType = getActiveScriptType(state);

  if (!userId || !fieldId) {
    return ''; // Hopefully will bounce to usable url
  }

  return versionRoute({
    state,
    route: `${shouldAddPluginPrefix()}/manual/${scriptType}/${step}/${userId}/${fieldId}${shouldAddClientId(userId)}`
  });
};

export const getCreateRoute = (state) => {
  const userId = getActiveUserId(state);
  const fieldId = getActiveFieldId(state);

  return getCreateRouteForUserIdFieldId({ state, userId, fieldId });
};

export const getEditRouteForUserIdFieldIdScriptId = ({
  state,
  userId,
  fieldId,
  scriptId
} = {}) => {
  if (!userId || !fieldId || !scriptId) {
    return ''; // Hopefully will bounce to usable url
  }

  return versionRoute({
    state,
    route: `${shouldAddPluginPrefix()}/edit/${userId}/${fieldId}/${scriptId}${shouldAddClientId(userId)}`
  });
};

export const getEditRouteForScriptId = (state, scriptId) => {
  const userId = getActiveUserId(state);
  const fieldId = getActiveFieldId(state);
  return getEditRouteForUserIdFieldIdScriptId({
    state,
    userId,
    fieldId,
    scriptId
  });
};

export const getEditRouteFromScript = (state, script) => {
  if (!(state && script)) {
    return null;
  }
  return getEditRouteForScriptId(state, script.id);
};

export const getManageRouteForUserIdFieldIdScriptId = ({
  state,
  userId,
  fieldId,
  scriptId
} = {}) => {
  if (!userId || !fieldId) {
    return ''; // Hopefully will bounce to usable url
  }

  const scriptType = getActiveScriptType(state);
  if (scriptType === scriptTypes.EXPERIMENT) {
    window.location = '/static/fv2/#/experiments';
  } else if (scriptId) {
    return versionRoute({
      state,
      route: `${shouldAddPluginPrefix()}/manage/${userId}/${fieldId}/${scriptId}${shouldAddClientId(userId)}`
    });
  }

  return versionRoute(
    { state,
      route: `${shouldAddPluginPrefix()}/manage/${userId}/${fieldId}${shouldAddClientId(userId)}`
    }); // Case where script ID is not yet known, or no RX for a field.
};

export const getManageRoute = (state) => {
  const userId = getActiveUserId(state);
  const fieldId = getActiveFieldId(state);
  const scriptId = getActiveScriptId(state);
  return getManageRouteForUserIdFieldIdScriptId({
    state,
    userId,
    fieldId,
    scriptId
  });
};

const makeCleanPathname = ({ pathname }) => {
  // inject index.html because the S3 config doesn't work without it (?)
  if (!pathname.includes('index.html') && !isPluginMode()) {
    pathname = pathname.replace(/\/?$/, '/index.html');
  }
  return { pathname }; // object wrapping for consistency with other makeClean* functions
};

export const makeCleanUrl = ({ url, clientId }) => {
  const parsed = parseUrl(url);
  const fieldInfo = determineActiveField({ query: parsed.query });

  if(isPluginMode()) {
    if(!parsed.hash.match('client-id')) {
      parsed.set(
        'hash',
        `${parsed.hash}?client-id=${clientId}`
      );
    }
  } else {
    // strip field-id from query params, force client-id query param
    parsed.set(
      'query',
      parsed.query
      .replace(/field-id=[^&]*/, '')
      .replace(/client-id=[^&]*/, '')
      .replace(/^\??/, `?client-id=${clientId}&`)
      .replace(/\&$/, '')
    );
  }
  // inject index.html if needed
  const cleanPathname = makeCleanPathname({ pathname: parsed.pathname });
  parsed.set('pathname', cleanPathname.pathname);

  // Temp fix because Webview still uses legacy hashroute (#/123456/23456677/create) for create:
  if (fieldInfo && parsed.hash && parsed.hash.match(/create$/)) {
    parsed.set(
      'hash',
      getCreateRouteForUserIdFieldId({
        userId: clientId,
        fieldId: fieldInfo.id
      })
    );
  }

  // put field in hashroute if needed
  const needsHash = !parsed.hash || parsed.hash.length <= 2;
  if (fieldInfo && needsHash) {
    parsed.set(
      'hash',
      getManageRouteForUserIdFieldIdScriptId({
        userId: clientId,
        fieldId: fieldInfo.id
      })
    );
  } else if (!parsed.hash) {
    parsed.set('hash', '#/');
  }
  return parsed.href;
};

export const changeUrl = (href) => {
  if (window && window.history) {
    window.history.replaceState({}, null, href);
  }
};

export const navigateToUrl = (href) => {
  if (typeof window !== 'undefined') {
    window.location.href = href || getScriptCreatorUrl();
  }
};
